import theme from 'shell/themes';
import COUNTRY_LIST from 'utils/countryList';
import TIME_ZONE_LIST from 'utils/timeZoneList';
import CA_SCHOOL_LIST from 'utils/canadaSchoolList';
import US_SCHOOL_LIST from 'utils/usSchoolList';
import MAJOR_LIST from 'utils/majorList';
import SEMESTER_LIST from 'utils/semesterList';

export const emailRegex =
    // RFC 5322
    // /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    // JS version from emailregex.com
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const numberRegex = /^\d+$/;

export const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

export const PRONOUN_OPTIONS = Object.freeze([
    { title: 'He/Him', value: 'hehim' },
    { title: 'She/Her', value: 'sheher' },
    { title: 'They/Them', value: 'theythem' },
    { title: 'Ze/Zir', value: 'zezir' },
    { title: 'Other', value: 'other' },
    { title: 'Prefer not to say', value: 'undisclosed' },
]);

export const EN_PROFICIENCY_OPTIONS = Object.freeze([
    { title: 'Native Speaker', value: 'native' },
    { title: 'Advanced', value: 'advanced' },
    { title: 'Upper-intermediate', value: 'upperintermediate' },
    { title: 'Intermediate', value: 'intermediate' },
    { title: 'Lower-intermediate', value: 'lowerintermediate' },
    { title: 'Beginner', value: 'beginner' },
]);

export const PERMISSIONS = Object.freeze({
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
    STATUS: 'status',
});

export const TASK_WORKLOAD_OPTIONS = Object.freeze([
    { title: 'Low', value: 'low' },
    { title: 'Medium', value: 'medium' },
    { title: 'High', value: 'high' },
]);

export const TASK_STATUS_OPTIONS = Object.freeze([
    { title: 'To Do', value: 'toDo' },
    { title: 'In Progress', value: 'inProgress' },
    { title: 'Under Review', value: 'underReview' },
    { title: 'Completed', value: 'completed' },
]);

export const WEEKDAY_OPTIONS = Object.freeze([
    { title: 'Monday', value: 1 },
    { title: 'Tuesday', value: 2 },
    { title: 'Wednesday', value: 3 },
    { title: 'Thursday', value: 4 },
    { title: 'Friday', value: 5 },
    { title: 'Saturday', value: 6 },
    { title: 'Sunday', value: 7 },
]);

export const HOURS_OPTIONS = Object.freeze([
    { title: '00:00', value: 0 },
    { title: '01:00', value: 1 },
    { title: '02:00', value: 2 },
    { title: '03:00', value: 3 },
    { title: '04:00', value: 4 },
    { title: '05:00', value: 5 },
    { title: '06:00', value: 6 },
    { title: '07:00', value: 7 },
    { title: '08:00', value: 8 },
    { title: '09:00', value: 9 },
    { title: '10:00', value: 10 },
    { title: '11:00', value: 11 },
    { title: '12:00', value: 12 },
    { title: '13:00', value: 13 },
    { title: '14:00', value: 14 },
    { title: '15:00', value: 15 },
    { title: '16:00', value: 16 },
    { title: '17:00', value: 17 },
    { title: '18:00', value: 18 },
    { title: '19:00', value: 19 },
    { title: '20:00', value: 20 },
    { title: '21:00', value: 21 },
    { title: '22:00', value: 22 },
    { title: '23:00', value: 23 },
]);

export const DEFAULT_WEEKDAY_OPTION = Object.freeze({
    title: 'Friday',
    value: 5,
});

export const DEFAULT_HOUR_OPTION = Object.freeze({
    title: '17:00',
    value: 17,
});

export const FEEDBACK_MAX_LENGTH = 400;

export const GROUP_ROLE = Object.freeze({
    LEADER: 'leader',
    MEMBER: 'member',
    MANAGER: 'manager',
});

export const TYPES = Object.freeze({
    GROUP: 'group',
    FILE: 'file',
    TASK: 'task',
    SHARED_URL: 'shared_url',
    COURSE: 'course',
    ACCOUNT: 'account',
    FEEDBACK: 'feedback',
    SUBFEEDBACK: 'subfeedback',
    SUBTASK: 'subtask',
    CHAT: 'chat',
    TEMPLATE: 'template',
    MILESTONE: 'milestone',
    SUBMISSION: 'submission',
    PEER_EVALUATION: 'peer_evaluation',
    AI_FEEDBACK: 'ai_feedback',
});

export const ACTIVITY_ACTIONS = Object.freeze({
    LOGIN: 'login',
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
    STATUS: 'status',
    JOIN: 'join',
    LEAVE: 'leave',
    PAUSE: 'pause',
    UNPAUSE: 'unpause',
});

export const SUBMISSION_TYPES = Object.freeze({
    FILE: 'file',
    PLAINTEXT: 'plaintext',
    URL: 'url',
});

export { COUNTRY_LIST, TIME_ZONE_LIST, US_SCHOOL_LIST, CA_SCHOOL_LIST, MAJOR_LIST, SEMESTER_LIST };

export const DEFAULT_TASK_TITLE = 'Untitled Task';

export const DEFAULT_SUBTASK_TITLE = 'Untitled Subtask';

export const AVATAR_COLOR_OPTIONS = Object.freeze([
    { bgColor: theme.palette.ensightful.error[800], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.error[600], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.error[100], textColor: theme.palette.ensightful.error[900] },

    { bgColor: theme.palette.ensightful.success[700], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.success[400], textColor: theme.palette.ensightful.success[900] },
    { bgColor: theme.palette.ensightful.success[100], textColor: theme.palette.ensightful.success[900] },

    { bgColor: theme.palette.ensightful.warning[600], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.warning[500], textColor: theme.palette.ensightful.neutral[900] },
    { bgColor: theme.palette.ensightful.warning[100], textColor: theme.palette.ensightful.warning[900] },

    { bgColor: theme.palette.ensightful.secondary[800], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.secondary[400], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.secondary[100], textColor: theme.palette.ensightful.secondary[900] },

    { bgColor: theme.palette.ensightful.primary[800], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.primary[500], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.primary[100], textColor: theme.palette.ensightful.primary[900] },

    { bgColor: theme.palette.ensightful.neutral[800], textColor: theme.palette.ensightful.shades[0] },
    { bgColor: theme.palette.ensightful.neutral[500], textColor: theme.palette.ensightful.neutral[900] },
    { bgColor: theme.palette.ensightful.neutral[200], textColor: theme.palette.ensightful.neutral[900] },
]);

export const WORKLOAD = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
};

export const WORKLOAD_POINTS = {
    low: 1,
    medium: 2,
    high: 4,
};

export const STATUS_VALUES = {
    toDo: 1,
    inProgress: 2,
    underReview: 3,
    completed: 4,
};

export const MAX_AI_FEEDBACK_LIMIT = 3;
